"use client";
import { createLogAdapter } from "@/lib/logger/console-adapter";

import { useCallback, useEffect, useState } from "react";
import { useIdentity } from "@/contexts/IdentityContext";
import { parseVipDate } from "@/lib/vip/status";
import type { WhitelistUser } from "@/types/vip";

const uiLog = createLogAdapter("hooks/useVipWhitelist.ts");

export interface VipWhitelistData {
	isWhitelisted: boolean;
	whitelistInfo: WhitelistUser | null;
	isLoading: boolean;
	error: string | null;
	refresh: () => Promise<void>;
}

export interface UseVipWhitelistOptions {
	identityId?: string;
	enabled?: boolean;
}

export function useVipWhitelist(
	options: UseVipWhitelistOptions = {},
): VipWhitelistData {
	const [isWhitelisted, setIsWhitelisted] = useState(false);
	const [whitelistInfo, setWhitelistInfo] = useState<WhitelistUser | null>(
		null,
	);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const { principal, isAuthenticated } = useIdentity();
	const targetIdentityId = options.identityId ?? principal ?? null;
	const enabled = options.enabled ?? !!(options.identityId || isAuthenticated);

	const checkWhitelist = useCallback(async () => {
		if (!enabled || !targetIdentityId) {
			setIsWhitelisted(false);
			setWhitelistInfo(null);
			setError(null);
			return;
		}

		setIsLoading(true);
		setError(null);

		try {
			// 获取白名单数据
			const response = await fetch("/api/vip/whitelist", {
				cache: "no-store",
				headers: {
					"Cache-Control": "no-cache, no-store, must-revalidate",
				},
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const whitelist: WhitelistUser[] = await response.json();

			const userEntry = whitelist.find((entry) => entry.id === targetIdentityId);

			if (userEntry) {
				const today = new Date();
				const startDate = parseVipDate(userEntry.start_time);
				const endDate = parseVipDate(userEntry.end_time);

				if (today >= startDate && today <= endDate) {
					setIsWhitelisted(true);
					setWhitelistInfo(userEntry);
				} else {
					setIsWhitelisted(false);
					setWhitelistInfo(null);
				}
			} else {
				setIsWhitelisted(false);
				setWhitelistInfo(null);
			}
		} catch (err) {
			uiLog.error("Failed to check VIP whitelist:", err);
			setError("检查VIP状态失败");
			setIsWhitelisted(false);
			setWhitelistInfo(null);
		} finally {
			setIsLoading(false);
		}
	}, [enabled, targetIdentityId]);

	useEffect(() => {
		checkWhitelist();
	}, [checkWhitelist]);

	return {
		isWhitelisted,
		whitelistInfo,
		isLoading,
		error,
		refresh: checkWhitelist,
	};
}

export default useVipWhitelist;
